import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Dropdown,
  Empty,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  BarChartOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  EyeOutlined,
  FileTextOutlined,
  LogoutOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  createExamination,
  createLensPrescription,
  fillLensPrescription,
  getExaminations,
  getLensPrescriptions,
  getOphthalmologistProfile,
  getOpticianProfile,
  getPatientProfiles,
  registerPatient,
} from "../../services/ophthalmologyService";
import { createTicket, getMyTickets } from "../../services/supportService";
import { getMyInvoices, getMyUsageSummary } from "../../services/usageBillingService";

const { Header, Sider, Content } = Layout;
const { Title, Text }            = Typography;
const { Option }                 = Select;

const toArray = (d) => Array.isArray(d) ? d : (d?.results ?? []);

const FRAME_OPTIONS = [
  { value: "GLASSES",        label: "Glasses"          },
  { value: "CONTACT_LENSES", label: "Contact Lenses"   },
  { value: "BOTH",           label: "Glasses & Contacts"},
  { value: "SUNGLASSES",     label: "Sunglasses"       },
];

// ── Print lens prescription slip ─────────────────────────────────────────────
function printLensPrescription(rx, examLabel, clinicName) {
  const win = window.open("", "_blank", "width=700,height=600");
  const date = rx.filled_at
    ? new Date(rx.filled_at).toLocaleDateString()
    : new Date(rx.created_at).toLocaleDateString();
  win.document.write(`
    <html><head><title>Lens Prescription</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 32px; color: #222; }
      h2   { margin-bottom: 4px; }
      .sub { color: #888; font-size: 13px; margin-bottom: 24px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
      th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; font-size: 14px; }
      th { background: #f5f7fa; font-weight: 600; }
      .footer { font-size: 12px; color: #aaa; margin-top: 40px; border-top: 1px solid #eee; padding-top: 12px; }
    </style></head><body>
    <h2>MCHUSA DHN — Lens Prescription</h2>
    <div class="sub">${clinicName ?? "Ophthalmology Clinic"} &nbsp;|&nbsp; Issued: ${date}</div>
    <p><strong>Patient / Examination:</strong> ${examLabel}</p>
    <table>
      <tr><th></th><th>Sphere</th><th>Cylinder</th><th>Axis</th></tr>
      <tr><td><strong>Right Eye (OD)</strong></td>
          <td>${rx.right_eye_sphere}</td>
          <td>${rx.right_eye_cylinder}</td>
          <td>${rx.right_eye_axis}</td></tr>
      <tr><td><strong>Left Eye (OS)</strong></td>
          <td>${rx.left_eye_sphere}</td>
          <td>${rx.left_eye_cylinder}</td>
          <td>${rx.left_eye_axis}</td></tr>
    </table>
    <p><strong>Pupillary Distance:</strong> ${rx.pupillary_distance ?? "—"}</p>
    <p><strong>Frame Preference:</strong> ${rx.frame_preference ?? "—"}</p>
    <p><strong>Status:</strong> ${rx.is_filled ? "Filled" : "Pending Fill"}</p>
    <div class="footer">
      This prescription is generated by the MCHUSA Digital Healthcare Network.
      It is confidential PHI — do not share without patient consent.
    </div>
    </body></html>
  `);
  win.document.close();
  win.print();
}

// ── Main component ────────────────────────────────────────────────────────────
export default function OphthalmologyDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isOphthalmologist = user?.role === "OPHTHALMOLOGIST";
  const isOptician        = user?.role === "OPTICIAN";

  // ── Sidebar items (role-aware) ────────────────────────────────────────────
  const MENU_ITEMS = useMemo(() => {
    const items = [
      { key: "dashboard",         icon: <DashboardOutlined />,       label: "Dashboard"          },
      { key: "lens-prescriptions", icon: <FileTextOutlined />,       label: "Lens Prescriptions" },
      { key: "usage",             icon: <BarChartOutlined />,        label: "Usage & Invoices"   },
      { key: "support",           icon: <CustomerServiceOutlined />, label: "Support"             },
      { key: "profile",           icon: <UserOutlined />,            label: "My Profile"          },
    ];
    if (isOphthalmologist) {
      items.splice(1, 0,
        { key: "examinations", icon: <EyeOutlined />,    label: "Eye Examinations" },
        { key: "patients",     icon: <TeamOutlined />,   label: "Patients"         },
      );
    }
    return items;
  }, [isOphthalmologist]);

  // ── State ─────────────────────────────────────────────────────────────────
  const [collapsed,    setCollapsed]    = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("dashboard");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [retryCount,   setRetryCount]   = useState(0);
  const [submitting,   setSubmitting]   = useState(false);

  const [myProfile,          setMyProfile]          = useState(null);
  const [examinations,       setExaminations]       = useState([]);
  const [lensPrescriptions,  setLensPrescriptions]  = useState([]);
  const [patientProfiles,    setPatientProfiles]     = useState([]);

  // Exam modal
  const [examModalOpen,  setExamModalOpen]  = useState(false);
  const [viewExamTarget, setViewExamTarget] = useState(null);
  const [examForm]                          = Form.useForm();

  // Lens prescription modal
  const [lensModalOpen,    setLensModalOpen]    = useState(false);
  const [lensModalExamId,  setLensModalExamId]  = useState(null); // pre-fill when opened from row
  const [viewLensTarget,   setViewLensTarget]   = useState(null);
  const [lensForm]                              = Form.useForm();

  // Patient history modal
  const [viewPatientId, setViewPatientId] = useState(null);

  // Support
  const [myTickets,      setMyTickets]      = useState([]);
  const [ticketModal,    setTicketModal]    = useState(false);
  const [ticketDetail,   setTicketDetail]   = useState(null);
  const [supportLoaded,  setSupportLoaded]  = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);
  const [ticketForm]                        = Form.useForm();

  // Exam modal patient selector
  const [patients, setPatients] = useState([]);

  // Register patient
  const [regModal,             setRegModal]             = useState(false);
  const [regForm]                                        = Form.useForm();
  const [patientsFetching,     setPatientsFetching]     = useState(false);

  // Network patients modal
  const [networkPatientsModal, setNetworkPatientsModal] = useState(false);
  const [networkSearch,        setNetworkSearch]        = useState("");

  // Usage & Invoices
  const [usageLoaded,   setUsageLoaded]   = useState(false);
  const [usageLoading,  setUsageLoading]  = useState(false);
  const [usageSummary,  setUsageSummary]  = useState(null);
  const [myInvoices,    setMyInvoices]    = useState([]);
  const [invoiceDetail, setInvoiceDetail] = useState(null);

  // ── Derived / memoised ────────────────────────────────────────────────────
  const patientMap = useMemo(() => {
    const m = {};
    patientProfiles.forEach(p => { m[p.id] = p; });
    return m;
  }, [patientProfiles]);

  const examMap = useMemo(() => {
    const m = {};
    examinations.forEach(e => { m[e.id] = e; });
    return m;
  }, [examinations]);

  // examId -> LensPrescription (if one exists)
  const examPrescMap = useMemo(() => {
    const m = {};
    lensPrescriptions.forEach(p => { m[p.examination] = p; });
    return m;
  }, [lensPrescriptions]);

  // Unique patient IDs seen in examinations
  const uniquePatientIds = useMemo(() =>
    [...new Set(examinations.map(e => e.patient))],
    [examinations]
  );

  const now = new Date();
  const examsThisMonth = examinations.filter(e => {
    const d = new Date(e.examination_date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  // Examinations that don't yet have a lens prescription (for lens modal select)
  const examsWithoutRx = useMemo(() =>
    examinations.filter(e => !examPrescMap[e.id]),
    [examinations, examPrescMap]
  );

  // ── Helpers ───────────────────────────────────────────────────────────────
  const patientLabel = (patientId) => {
    const p = patientMap[patientId];
    if (!p) return String(patientId ?? "").slice(0, 8).toUpperCase();
    return `${p.user_email}${p.pin ? ` (PIN: ${p.pin})` : ""}`;
  };

  const examLabel = (examId) => {
    const e = examMap[examId];
    if (!e) return String(examId ?? "").slice(0, 8).toUpperCase();
    const d = new Date(e.examination_date).toLocaleDateString();
    return `${d} — ${patientLabel(e.patient)}`;
  };

  const clinicName = isOphthalmologist
    ? myProfile?.clinic_name
    : myProfile?.shop_name;

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadExaminations = useCallback(async () => {
    const res = await getExaminations();
    setExaminations(toArray(res.data).map(e => ({ ...e, _rowKey: e.id })));
  }, []);

  const loadLensPrescriptions = useCallback(async () => {
    const res = await getLensPrescriptions();
    setLensPrescriptions(toArray(res.data).map(p => ({ ...p, _rowKey: p.id })));
  }, []);

  const loadSupport = useCallback(async () => {
    setSupportLoading(true);
    try {
      const res = await getMyTickets();
      setMyTickets(toArray(res.data).map(r => ({ ...r, key: r.id })));
      setSupportLoaded(true);
    } catch { /* silent */ } finally { setSupportLoading(false); }
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await getPatientProfiles();
      const data = response.data?.results || response.data || [];
      setPatients(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error("Failed to load patients");
    }
  };

  const fetchNetworkPatients = useCallback(async () => {
    setPatientsFetching(true);
    try {
      const res = await getPatientProfiles({ is_active: true });
      setPatientProfiles(toArray(res.data));
    } catch { /* silent */ } finally {
      setPatientsFetching(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    setLoading(true);
    setError(null);

    const profileFetch = isOphthalmologist
      ? getOphthalmologistProfile()
      : getOpticianProfile();

    const calls = [profileFetch, loadLensPrescriptions()];
    if (isOphthalmologist) {
      calls.push(loadExaminations());
      calls.push(fetchNetworkPatients());
    }

    Promise.allSettled(calls)
      .then(([profileResult]) => {
        if (profileResult.status === "fulfilled") {
          const profiles = toArray(profileResult.value?.data);
          setMyProfile(profiles[0] ?? null);
        }
      })
      .catch(() => setError("Failed to load data. Please refresh."))
      .finally(() => setLoading(false));
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const [summaryRes, invoicesRes] = await Promise.all([
        getMyUsageSummary(),
        getMyInvoices(),
      ]);
      const rows = toArray(summaryRes.data);
      setUsageSummary(rows[0] ?? null);
      setMyInvoices(toArray(invoicesRes.data).map(r => ({ ...r, key: r.id })));
      setUsageLoaded(true);
    } catch { /* silent */ } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedMenu === "support" && !supportLoaded) loadSupport();
  }, [selectedMenu, supportLoaded, loadSupport]);

  useEffect(() => {
    if (selectedMenu === "usage" && !usageLoaded) loadUsage();
  }, [selectedMenu, usageLoaded, loadUsage]);

  useEffect(() => {
    if (examModalOpen) fetchPatients();
  }, [examModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleLogout = () => { logout(); navigate("/login"); };

  const openLensModal = (examId = null) => {
    setLensModalExamId(examId);
    lensForm.resetFields();
    if (examId) {
      lensForm.setFieldValue("examination", examId);
    }
    setLensModalOpen(true);
  };

  const handleCreateExam = async (values) => {
    setSubmitting(true);
    try {
      await createExamination({
        patient:           values.patient,
        examination_date:  values.examination_date.toISOString(),
        right_eye_vision:  values.right_eye_vision,
        left_eye_vision:   values.left_eye_vision,
        diagnosis:         values.diagnosis,
        recommendations:   values.recommendations,
        notes:             values.notes ?? "",
      });
      message.success("Examination recorded.");
      setExamModalOpen(false);
      examForm.resetFields();
      await loadExaminations();
    } catch (err) {
      const d = err?.response?.data;
      message.error(
        d?.patient?.[0] ?? d?.examination_date?.[0] ?? d?.detail ?? "Failed to record examination."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateLens = async (values) => {
    setSubmitting(true);
    try {
      await createLensPrescription({
        examination:       values.examination,
        right_eye_sphere:  values.right_eye_sphere,
        right_eye_cylinder: values.right_eye_cylinder,
        right_eye_axis:    values.right_eye_axis,
        left_eye_sphere:   values.left_eye_sphere,
        left_eye_cylinder: values.left_eye_cylinder,
        left_eye_axis:     values.left_eye_axis,
        pupillary_distance: values.pupillary_distance ?? "",
        frame_preference:  values.frame_preference ?? "",
      });
      message.success("Lens prescription issued.");
      setLensModalOpen(false);
      lensForm.resetFields();
      setLensModalExamId(null);
      await loadLensPrescriptions();
    } catch (err) {
      const d = err?.response?.data;
      message.error(
        d?.examination?.[0] ?? d?.detail ?? "Failed to issue lens prescription."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleFill = (rx) => {
    Modal.confirm({
      title:   "Mark as Filled",
      content: `Mark lens prescription for ${examLabel(rx.examination)} as filled?`,
      okText:  "Fill",
      onOk: async () => {
        try {
          await fillLensPrescription(rx.id);
          message.success("Prescription marked as filled.");
          await loadLensPrescriptions();
        } catch (err) {
          message.error(err?.response?.data?.detail ?? "Failed to fill prescription.");
        }
      },
    });
  };

  const handleCreateTicket = async (values) => {
    try {
      await createTicket(values);
      message.success("Ticket submitted.");
      setTicketModal(false);
      ticketForm.resetFields();
      loadSupport();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to submit ticket.");
    }
  };

  const handleRegisterPatient = async (values) => {
    setSubmitting(true);
    try {
      const res = await registerPatient({
        email:      values.email,
        first_name: values.first_name,
        last_name:  values.last_name,
        phone:      values.phone || "",
        password:   values.password,
      });
      setRegModal(false);
      regForm.resetFields();
      Modal.success({
        title:   "Patient Registered Successfully",
        width:   460,
        content: (
          <div>
            <p style={{ margin: "0 0 8px" }}>The patient has been registered.</p>
            <p style={{ margin: "0 0 12px" }}><strong>Email:</strong> {res.data.email}</p>
            <div style={{
              background: "#f6ffed", border: "1px solid #b7eb8f",
              borderRadius: 6, padding: "12px 16px",
            }}>
              <p style={{ margin: 0, fontWeight: 600, color: "#237804" }}>Patient PIN</p>
              <p style={{
                margin: "6px 0 0", fontSize: 32, letterSpacing: 8,
                fontFamily: "monospace", color: "#389e0d", fontWeight: 700,
              }}>
                {res.data.pin}
              </p>
            </div>
            <p style={{ color: "#8c8c8c", fontSize: 12, margin: "10px 0 0" }}>
              Provide this PIN to the patient for Kiosk access. It will not be shown again.
            </p>
          </div>
        ),
      });
      await fetchNetworkPatients();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.email?.[0] ?? d?.detail ?? "Failed to register patient.");
    } finally { setSubmitting(false); }
  };

  // ── Column definitions ────────────────────────────────────────────────────

  const examColumns = [
    {
      title: "Patient", key: "patient", ellipsis: true,
      render: (_, r) => <Text>{patientLabel(r.patient)}</Text>,
    },
    {
      title: "Date", dataIndex: "examination_date", key: "examination_date", width: 110,
      render: d => new Date(d).toLocaleDateString(),
    },
    {
      title: "Vision L", dataIndex: "left_eye_vision", key: "left_eye_vision", width: 95,
      render: v => <Text code>{v}</Text>,
    },
    {
      title: "Vision R", dataIndex: "right_eye_vision", key: "right_eye_vision", width: 95,
      render: v => <Text code>{v}</Text>,
    },
    {
      title: "Diagnosis", dataIndex: "diagnosis", key: "diagnosis", ellipsis: true,
    },
    {
      title: "Lens Rx", key: "lens_rx", width: 130,
      render: (_, r) => {
        const rx = examPrescMap[r.id];
        return rx
          ? <Tag color="blue">Issued</Tag>
          : <Tag color="default">None</Tag>;
      },
    },
    {
      title: "Actions", key: "actions", width: 190, fixed: "right",
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" onClick={() => setViewExamTarget(r)}>View</Button>
          {isOphthalmologist && !examPrescMap[r.id] && (
            <Button size="small" type="primary" ghost
              onClick={() => openLensModal(r.id)}>
              Issue Lens Rx
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const lensColumns = [
    {
      title: "Examination", key: "examination", ellipsis: true,
      render: (_, r) => <Text>{examLabel(r.examination)}</Text>,
    },
    {
      title: "Sphere L", dataIndex: "left_eye_sphere",  key: "left_eye_sphere",  width: 90,
      render: v => <Text code>{v}</Text>,
    },
    {
      title: "Sphere R", dataIndex: "right_eye_sphere", key: "right_eye_sphere", width: 90,
      render: v => <Text code>{v}</Text>,
    },
    {
      title: "Frame Pref", dataIndex: "frame_preference", key: "frame_preference", width: 130,
      render: v => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: "Status", key: "status", width: 100,
      render: (_, r) => r.is_filled
        ? <Tag color="green">Filled</Tag>
        : <Tag color="orange">Pending</Tag>,
    },
    {
      title: "Actions", key: "actions", width: 190, fixed: "right",
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" onClick={() => setViewLensTarget(r)}>View</Button>
          {isOptician && !r.is_filled && (
            <Button size="small" type="primary" onClick={() => handleFill(r)}>Fill</Button>
          )}
          <Button size="small" icon={<PrinterOutlined />}
            onClick={() => printLensPrescription(r, examLabel(r.examination), clinicName)}>
            Print
          </Button>
        </Space>
      ),
    },
  ];

  const patientColumns = [
    {
      title: "Patient", key: "email", ellipsis: true,
      render: (_, r) => {
        const p = patientMap[r.patientId];
        return <Text>{p?.user_email ?? String(r.patientId).slice(0, 8)}</Text>;
      },
    },
    {
      title: "PIN", key: "pin",  width: 110,
      render: (_, r) => <Text code>{patientMap[r.patientId]?.pin ?? "—"}</Text>,
    },
    {
      title: "Total Exams", key: "count", width: 110,
      render: (_, r) => r.examCount,
    },
    {
      title: "Last Visit", key: "lastVisit", width: 120,
      render: (_, r) => new Date(r.lastVisit).toLocaleDateString(),
    },
    {
      title: "Lens Rx", key: "hasLens", width: 100,
      render: (_, r) => r.hasLens
        ? <Tag color="blue">Issued</Tag>
        : <Tag color="default">None</Tag>,
    },
    {
      title: "History", key: "history", width: 90,
      render: (_, r) => (
        <Button size="small" onClick={() => setViewPatientId(r.patientId)}>View</Button>
      ),
    },
  ];

  // ── Derived patient rows for Patients view ────────────────────────────────
  const patientRows = useMemo(() => {
    return uniquePatientIds.map(pid => {
      const exams = examinations.filter(e => e.patient === pid);
      const sorted = [...exams].sort(
        (a, b) => new Date(b.examination_date) - new Date(a.examination_date)
      );
      const hasLens = exams.some(e => examPrescMap[e.id]);
      return {
        _rowKey:   pid,
        patientId: pid,
        examCount: exams.length,
        lastVisit: sorted[0]?.examination_date,
        hasLens,
      };
    });
  }, [uniquePatientIds, examinations, examPrescMap]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const ophStats = [
    { title: "Total Examinations",       value: examinations.length,      color: "#1677ff" },
    { title: "Lens Prescriptions Issued", value: lensPrescriptions.length, color: "#722ed1" },
    { title: "Patients Seen",            value: uniquePatientIds.length,  color: "#13c2c2" },
    { title: "Exams This Month",         value: examsThisMonth.length,    color: "#52c41a" },
  ];

  const opticianStats = [
    { title: "Prescriptions to Fill", value: lensPrescriptions.filter(p => !p.is_filled).length, color: "#fa8c16" },
    { title: "Filled by Me",          value: lensPrescriptions.filter(p => p.is_filled).length,  color: "#52c41a" },
    { title: "Total",                 value: lensPrescriptions.length,                            color: "#1677ff" },
  ];

  const stats = isOphthalmologist ? ophStats : opticianStats;

  // ── Views ─────────────────────────────────────────────────────────────────
  function renderContent() {
    switch (selectedMenu) {

      // ── Dashboard home ───────────────────────────────────────────────────
      case "dashboard":
        return (
          <>
            {/* Register New Patient shortcut — OPHTHALMOLOGIST only */}
            {isOphthalmologist && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                <Button type="primary" icon={<UserAddOutlined />}
                  onClick={() => setRegModal(true)}>
                  Register New Patient
                </Button>
              </div>
            )}

            {/* Profile missing warning for ophthalmologist */}
            {isOphthalmologist && !myProfile && !loading && (
              <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                message="No ophthalmologist profile found. You must create a profile before recording examinations."
                style={{ marginBottom: 16 }}
              />
            )}

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {stats.map(({ title, value, color }) => (
                <Col xs={24} sm={12} lg={6} key={title}>
                  <Card variant="borderless" style={{ borderRadius: 8 }}>
                    <Statistic
                      title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>{title}</Text>}
                      value={value}
                      styles={{ content: { color, fontSize: 28, fontWeight: 600 } }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Recent Examinations — OPHTHALMOLOGIST only */}
            {isOphthalmologist && (
              <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}
                title={<Text strong>Recent Examinations</Text>}
                extra={
                  <Button type="primary" size="small" icon={<PlusCircleOutlined />}
                    onClick={() => { examForm.resetFields(); setExamModalOpen(true); }}>
                    New Examination
                  </Button>
                }
              >
                <Table
                  rowKey="_rowKey"
                  columns={examColumns}
                  dataSource={examinations.slice(0, 8)}
                  pagination={false}
                  size="small"
                  scroll={{ x: 900 }}
                  locale={{ emptyText: <Empty description="No examinations yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                />
              </Card>
            )}

            {/* Recent Lens Prescriptions */}
            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={<Text strong>Recent Lens Prescriptions</Text>}
              extra={
                isOphthalmologist && (
                  <Button type="primary" size="small" icon={<PlusCircleOutlined />}
                    onClick={() => openLensModal()}>
                    Issue Prescription
                  </Button>
                )
              }
            >
              <Table
                rowKey="_rowKey"
                columns={lensColumns}
                dataSource={lensPrescriptions.slice(0, 8)}
                pagination={false}
                size="small"
                scroll={{ x: 900 }}
                locale={{ emptyText: <Empty description="No lens prescriptions yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              />
            </Card>
          </>
        );

      // ── Eye Examinations ─────────────────────────────────────────────────
      case "examinations":
        return (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0, color: "#262626" }}>Eye Examinations</Title>
              <Button type="primary" icon={<PlusCircleOutlined />}
                onClick={() => { examForm.resetFields(); setExamModalOpen(true); }}>
                New Examination
              </Button>
            </div>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Table
                rowKey="_rowKey"
                columns={examColumns}
                dataSource={examinations}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                size="small"
                scroll={{ x: 900 }}
                locale={{ emptyText: <Empty description="No examinations found" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              />
            </Card>
          </>
        );

      // ── Lens Prescriptions ───────────────────────────────────────────────
      case "lens-prescriptions":
        return (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0, color: "#262626" }}>Lens Prescriptions</Title>
              {isOphthalmologist && (
                <Button type="primary" icon={<PlusCircleOutlined />}
                  onClick={() => openLensModal()}>
                  Issue Prescription
                </Button>
              )}
            </div>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Table
                rowKey="_rowKey"
                columns={lensColumns}
                dataSource={lensPrescriptions}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                size="small"
                scroll={{ x: 950 }}
                locale={{ emptyText: <Empty description="No lens prescriptions found" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              />
            </Card>
          </>
        );

      // ── Patients ─────────────────────────────────────────────────────────
      case "patients":
        return (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0, color: "#262626" }}>Patients</Title>
              <Space>
                <Button icon={<TeamOutlined />}
                  onClick={() => setNetworkPatientsModal(true)}>
                  View Network Patients
                </Button>
                <Button type="primary" icon={<UserAddOutlined />}
                  onClick={() => setRegModal(true)}>
                  Register New Patient
                </Button>
              </Space>
            </div>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Table
                rowKey="_rowKey"
                columns={patientColumns}
                dataSource={patientRows}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                size="small"
                locale={{ emptyText: <Empty description="No patients seen yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
              />
            </Card>
          </>
        );

      // ── Usage & Invoices ─────────────────────────────────────────────────
      case "usage": {
        const INVOICE_COLOR = { DRAFT: "default", SENT: "blue", PAID: "green", OVERDUE: "red" };

        const fmtMonth = (ym) => {
          if (!ym) return "—";
          const [y, m] = ym.split("-");
          return new Date(Number(y), Number(m) - 1, 1)
            .toLocaleString("default", { month: "long", year: "numeric" });
        };

        const regCount    = usageSummary?.registrations              ?? 0;
        const eyeCount    = usageSummary?.eye_examinations           ?? 0;
        const lensCount   = usageSummary?.lens_prescriptions_issued  ?? 0;
        const estFee      = parseFloat(usageSummary?.estimated_fee   ?? "0").toFixed(2);

        // Ophthalmologist: 4 cards at sm=6; Optician: 2 cards at sm=12
        const colSpan = isOphthalmologist ? 6 : 12;

        return (
          <Spin spinning={usageLoading} description="Loading…">
            {/* ── Current Month Summary ── */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {isOphthalmologist && (
                <>
                  <Col xs={24} sm={colSpan}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Statistic
                        title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Patients Registered This Month</Text>}
                        value={regCount}
                        styles={{ content: { color: "#13c2c2", fontSize: 28, fontWeight: 600 } }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={colSpan}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Statistic
                        title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Eye Examinations This Month</Text>}
                        value={eyeCount}
                        styles={{ content: { color: "#1677ff", fontSize: 28, fontWeight: 600 } }}
                      />
                    </Card>
                  </Col>
                </>
              )}
              <Col xs={24} sm={colSpan}>
                <Card variant="borderless" style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Lens Prescriptions This Month</Text>}
                    value={lensCount}
                    styles={{ content: { color: "#722ed1", fontSize: 28, fontWeight: 600 } }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={colSpan}>
                <Card variant="borderless" style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Estimated Fee This Month</Text>}
                    value={`$${estFee}`}
                    styles={{ content: { color: "#fa8c16", fontSize: 28, fontWeight: 600 } }}
                  />
                </Card>
              </Col>
            </Row>

            <Alert
              type="info"
              showIcon
              message="Your monthly invoice is generated by MCHUSA billing staff at the end of each month. Contact billing@mchusa.com for queries."
              style={{ marginBottom: 24 }}
            />

            {/* ── Invoice History ── */}
            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={<Text strong>Invoice History</Text>}>
              <Table
                rowKey="key"
                size="small"
                dataSource={myInvoices}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                locale={{ emptyText: "No invoices yet. Your first invoice will appear at end of billing month." }}
                columns={[
                  { title: "Month",   dataIndex: "billing_month", key: "month", width: 140,
                    render: v => fmtMonth(v) },
                  ...(isOphthalmologist ? [{
                    title: "Eye Exams", dataIndex: "total_eye_examinations", key: "exams", width: 110,
                  }] : []),
                  { title: "Lens Rx",  dataIndex: "total_lens_prescriptions",  key: "lens",  width: 100 },
                  { title: "Total ($)", dataIndex: "total_amount", key: "total", width: 110,
                    render: v => <Text strong>${parseFloat(v).toFixed(2)}</Text> },
                  { title: "Status", dataIndex: "status", key: "status", width: 100,
                    render: s => <Tag color={INVOICE_COLOR[s] ?? "default"}>{s}</Tag> },
                  { title: "Actions", key: "actions", width: 110, fixed: "right",
                    render: (_, r) => (
                      <Button size="small" onClick={() => setInvoiceDetail(r)}>View Details</Button>
                    ),
                  },
                ]}
              />
            </Card>
          </Spin>
        );
      }

      // ── Support ──────────────────────────────────────────────────────────
      case "support":
        return (
          <Spin spinning={supportLoading}>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Support</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}
              extra={
                <Button icon={<PlusCircleOutlined />} type="primary" size="small"
                  onClick={() => setTicketModal(true)}>
                  Report an Issue
                </Button>
              }
            >
              <Table
                size="small" dataSource={myTickets}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                locale={{ emptyText: "No tickets yet" }}
                columns={[
                  { title: "ID",       dataIndex: "id",         key: "id",        width: 90,
                    render: id => <Text code style={{ fontSize: 11 }}>{String(id).slice(0, 8)}</Text> },
                  { title: "Subject",  dataIndex: "subject",    key: "subject",   ellipsis: true },
                  { title: "Priority", dataIndex: "priority",   key: "priority",  width: 100,
                    render: p => <Tag color={{ LOW: "default", MEDIUM: "blue", HIGH: "orange", CRITICAL: "red" }[p] ?? "default"}>{p}</Tag> },
                  { title: "Status",   dataIndex: "status",     key: "status",    width: 120,
                    render: s => <Tag color={{ OPEN: "blue", IN_PROGRESS: "orange", RESOLVED: "green", CLOSED: "default" }[s] ?? "default"}>{s?.replace(/_/g, " ")}</Tag> },
                  { title: "Created",  dataIndex: "created_at", key: "created_at", width: 110,
                    render: d => d ? new Date(d).toLocaleDateString() : "—" },
                  { title: "Actions",  key: "actions", width: 80,
                    render: (_, r) => <Button size="small" onClick={() => setTicketDetail(r)}>View</Button> },
                ]}
              />
            </Card>
          </Spin>
        );

      // ── My Profile ───────────────────────────────────────────────────────
      case "profile":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>My Profile</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              {myProfile ? (
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="Email">{user?.email ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Role">
                    <Tag color="purple">{user?.role}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="License Number">{myProfile.license_number ?? "—"}</Descriptions.Item>
                  {isOphthalmologist && (
                    <>
                      <Descriptions.Item label="Specialty">{myProfile.specialty ?? "—"}</Descriptions.Item>
                      <Descriptions.Item label="Clinic Name">{myProfile.clinic_name ?? "—"}</Descriptions.Item>
                      <Descriptions.Item label="Clinic Address">{myProfile.clinic_address ?? "—"}</Descriptions.Item>
                    </>
                  )}
                  {isOptician && (
                    <>
                      <Descriptions.Item label="Shop Name">{myProfile.shop_name ?? "—"}</Descriptions.Item>
                      <Descriptions.Item label="Shop Address">{myProfile.shop_address ?? "—"}</Descriptions.Item>
                    </>
                  )}
                  <Descriptions.Item label="Phone">{myProfile.phone ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Verified">
                    {myProfile.is_verified
                      ? <Tag color="green">Verified</Tag>
                      : <Tag color="orange">Pending Verification</Tag>}
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <Empty description="No profile found. Please contact your system administrator." />
              )}
            </Card>
            <ChangePasswordForm />
          </>
        );

      default:
        return null;
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const userMenuItems = [
    { key: "logout", icon: <LogoutOutlined />, label: "Logout", danger: true, onClick: handleLogout },
  ];

  // Patient history exams (for patient detail modal)
  const patientHistoryExams = viewPatientId
    ? examinations.filter(e => e.patient === viewPatientId)
    : [];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        collapsible collapsed={collapsed} onCollapse={setCollapsed}
        width={215}
        style={{ background: "#fff", borderRight: "1px solid #f0f0f0" }}
      >
        <div style={{
          height: 64, display: "flex", alignItems: "center", justifyContent: "center",
          borderBottom: "1px solid #f0f0f0", padding: "0 12px",
        }}>
          {!collapsed && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
              <Text strong style={{ color: "#722ed1", fontSize: 13, whiteSpace: "nowrap" }}>Digital Healthcare Network</Text>
              <Text style={{ color: "#722ed1", fontSize: 12, whiteSpace: "nowrap" }}>Ophthalmology</Text>
            </div>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedMenu]}
          onClick={({ key }) => setSelectedMenu(key)}
          style={{ border: "none", marginTop: 8 }}
          items={MENU_ITEMS}
        />
      </Sider>

      <Layout>
        <Header style={{
          background: "#fff", padding: "0 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid #f0f0f0",
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <div>
            <Text strong style={{ fontSize: 16, color: "#722ed1" }}>MCHUSA</Text>
            <Text style={{ fontSize: 14, color: "#8c8c8c", marginLeft: 8 }}>
              | Ophthalmology &amp; Optical Services
            </Text>
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
            <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <Avatar size={32} icon={<UserOutlined />} style={{ background: "#722ed1" }} />
              <Text style={{ fontSize: 14, color: "#262626" }}>{user?.email}</Text>
            </div>
          </Dropdown>
        </Header>

        <Content style={{ padding: 24, background: "#f5f7fa" }}>
          {loading ? (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {[1, 2, 3, 4].map(i => (
                  <Col xs={24} sm={12} lg={6} key={i}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </Card>
                  </Col>
                ))}
              </Row>
              <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 24 }}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </Card>
            </>
          ) : error ? (
            <Alert type="error" message={error} showIcon style={{ marginBottom: 24 }}
              action={<Button size="small" onClick={() => setRetryCount(c => c + 1)}>Retry</Button>}
              onClose={() => setError(null)} closable />
          ) : renderContent()}
        </Content>
      </Layout>

      {/* ── New Examination Modal ── */}
      <Modal
        title="Record Eye Examination"
        open={examModalOpen}
        onOk={() => examForm.submit()}
        onCancel={() => { setExamModalOpen(false); examForm.resetFields(); }}
        okText="Save Examination"
        confirmLoading={submitting}

        destroyOnHidden
        width={640}
      >
        <Form form={examForm} layout="vertical" onFinish={handleCreateExam}>
          <Row gutter={16}>
            <Col span={14}>
              <Form.Item name="patient" label="Patient"
                rules={[{ required: true, message: "Select a patient" }]}>
                <Select showSearch placeholder="Select patient"
                  filterOption={(input, opt) =>
                    String(opt?.children ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={<Empty description="No patients found" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                >
                  {patients.map(p => (
                    <Select.Option key={p.id} value={p.id}>
                      {p.user_email} (PIN: {p.pin})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="examination_date" label="Examination Date"
                rules={[{ required: true, message: "Required" }]}>
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="left_eye_vision" label="Visual Acuity — Left Eye (OS)"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="e.g. 20/20 or 6/6" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="right_eye_vision" label="Visual Acuity — Right Eye (OD)"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="e.g. 20/40 or 6/12" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="diagnosis" label="Diagnosis"
            rules={[{ required: true, message: "Required" }]}>
            <Input.TextArea rows={2} placeholder="Clinical diagnosis" />
          </Form.Item>

          <Form.Item name="recommendations" label="Recommendations"
            rules={[{ required: true, message: "Required" }]}>
            <Input.TextArea rows={2} placeholder="Treatment recommendations, follow-up, etc." />
          </Form.Item>

          <Form.Item name="notes" label={
            <span>Notes <Tag color="orange" style={{ fontSize: 11 }}>PHI</Tag></span>
          }>
            <Input.TextArea rows={2} placeholder="Additional clinical notes (optional)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── View Examination Modal ── */}
      <Modal
        title="Examination Details"
        open={Boolean(viewExamTarget)}
        onCancel={() => setViewExamTarget(null)}
        footer={
          <Space>
            {isOphthalmologist && viewExamTarget && !examPrescMap[viewExamTarget.id] && (
              <Button type="primary" ghost onClick={() => {
                setViewExamTarget(null);
                openLensModal(viewExamTarget.id);
              }}>
                Issue Lens Prescription
              </Button>
            )}
            <Button onClick={() => setViewExamTarget(null)}>Close</Button>
          </Space>
        }
 destroyOnHidden width={560}
      >
        {viewExamTarget && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Patient">{patientLabel(viewExamTarget.patient)}</Descriptions.Item>
            <Descriptions.Item label="Date">
              {new Date(viewExamTarget.examination_date).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Visual Acuity — Left (OS)">
              <Text code>{viewExamTarget.left_eye_vision}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Visual Acuity — Right (OD)">
              <Text code>{viewExamTarget.right_eye_vision}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Diagnosis">{viewExamTarget.diagnosis}</Descriptions.Item>
            <Descriptions.Item label="Recommendations">{viewExamTarget.recommendations}</Descriptions.Item>
            {viewExamTarget.notes && (
              <Descriptions.Item label="Notes">{viewExamTarget.notes}</Descriptions.Item>
            )}
            <Descriptions.Item label="Lens Prescription">
              {examPrescMap[viewExamTarget.id]
                ? <Tag color="blue">Issued</Tag>
                : <Tag color="default">Not Issued</Tag>}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* ── Issue Lens Prescription Modal ── */}
      <Modal
        title="Issue Lens Prescription"
        open={lensModalOpen}
        onOk={() => lensForm.submit()}
        onCancel={() => { setLensModalOpen(false); lensForm.resetFields(); setLensModalExamId(null); }}
        okText="Issue Prescription"
        confirmLoading={submitting}

        destroyOnHidden
        width={680}
      >
        <Form form={lensForm} layout="vertical" onFinish={handleCreateLens}>
          <Form.Item name="examination" label="Examination"
            rules={[{ required: true, message: "Select an examination" }]}>
            <Select
              placeholder="Select examination"
              disabled={Boolean(lensModalExamId)}
              showSearch
              filterOption={(input, opt) =>
                String(opt?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent={
                <Empty description="No examinations available (all have prescriptions)"
                  image={Empty.PRESENTED_IMAGE_SIMPLE} />
              }
            >
              {(lensModalExamId
                ? examinations.filter(e => e.id === lensModalExamId)
                : examsWithoutRx
              ).map(e => (
                <Option key={e.id} value={e.id}>
                  {new Date(e.examination_date).toLocaleDateString()} — {patientLabel(e.patient)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={24}>
              <Text strong style={{ display: "block", marginBottom: 8, color: "#8c8c8c", fontSize: 12 }}>
                RIGHT EYE (OD)
              </Text>
            </Col>
            <Col span={8}>
              <Form.Item name="right_eye_sphere" label="Sphere"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="+1.25 or -0.50" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="right_eye_cylinder" label="Cylinder"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="-0.25" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="right_eye_axis" label="Axis"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="180" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Text strong style={{ display: "block", marginBottom: 8, color: "#8c8c8c", fontSize: 12 }}>
                LEFT EYE (OS)
              </Text>
            </Col>
            <Col span={8}>
              <Form.Item name="left_eye_sphere" label="Sphere"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="+1.25 or -0.50" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="left_eye_cylinder" label="Cylinder"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="-0.25" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="left_eye_axis" label="Axis"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="175" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="pupillary_distance" label="Pupillary Distance (mm)">
                <Input placeholder="e.g. 63.5 or 32/31" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="frame_preference" label="Frame Preference">
                <Select placeholder="Select type" allowClear>
                  {FRAME_OPTIONS.map(o => (
                    <Option key={o.value} value={o.value}>{o.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── View Lens Prescription Modal ── */}
      <Modal
        title="Lens Prescription Details"
        open={Boolean(viewLensTarget)}
        onCancel={() => setViewLensTarget(null)}
        footer={
          <Space>
            <Button icon={<PrinterOutlined />}
              onClick={() => viewLensTarget && printLensPrescription(
                viewLensTarget, examLabel(viewLensTarget.examination), clinicName
              )}>
              Print
            </Button>
            {isOptician && viewLensTarget && !viewLensTarget.is_filled && (
              <Button type="primary" onClick={() => { setViewLensTarget(null); handleFill(viewLensTarget); }}>
                Fill Prescription
              </Button>
            )}
            <Button onClick={() => setViewLensTarget(null)}>Close</Button>
          </Space>
        }
 destroyOnHidden width={540}
      >
        {viewLensTarget && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Examination" span={2}>
              {examLabel(viewLensTarget.examination)}
            </Descriptions.Item>
            <Descriptions.Item label="Right Sphere">
              <Text code>{viewLensTarget.right_eye_sphere}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Left Sphere">
              <Text code>{viewLensTarget.left_eye_sphere}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Right Cylinder">
              <Text code>{viewLensTarget.right_eye_cylinder}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Left Cylinder">
              <Text code>{viewLensTarget.left_eye_cylinder}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Right Axis">
              <Text code>{viewLensTarget.right_eye_axis}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Left Axis">
              <Text code>{viewLensTarget.left_eye_axis}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="PD">
              {viewLensTarget.pupillary_distance || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Frame Pref">
              {viewLensTarget.frame_preference
                ? <Tag>{viewLensTarget.frame_preference}</Tag>
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {viewLensTarget.is_filled
                ? <Tag color="green">Filled</Tag>
                : <Tag color="orange">Pending</Tag>}
            </Descriptions.Item>
            {viewLensTarget.filled_at && (
              <Descriptions.Item label="Filled At">
                {new Date(viewLensTarget.filled_at).toLocaleString()}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* ── Patient History Modal ── */}
      <Modal
        title={`Patient History — ${patientLabel(viewPatientId)}`}
        open={Boolean(viewPatientId)}
        onCancel={() => setViewPatientId(null)}
        footer={<Button onClick={() => setViewPatientId(null)}>Close</Button>}
 destroyOnHidden width={720}
      >
        <Table
          rowKey="_rowKey"
          size="small"
          dataSource={patientHistoryExams.map(e => ({ ...e, _rowKey: e.id }))}
          pagination={false}
          locale={{ emptyText: "No examinations found" }}
          columns={[
            { title: "Date", dataIndex: "examination_date", key: "date", width: 110,
              render: d => new Date(d).toLocaleDateString() },
            { title: "Vision L", dataIndex: "left_eye_vision",  key: "vl", width: 90,
              render: v => <Text code>{v}</Text> },
            { title: "Vision R", dataIndex: "right_eye_vision", key: "vr", width: 90,
              render: v => <Text code>{v}</Text> },
            { title: "Diagnosis", dataIndex: "diagnosis", key: "dx", ellipsis: true },
            { title: "Lens Rx", key: "rx", width: 100,
              render: (_, r) => examPrescMap[r.id]
                ? <Tag color="blue">Issued</Tag>
                : <Tag color="default">None</Tag> },
          ]}
        />
      </Modal>

      {/* ── New Support Ticket Modal ── */}
      <Modal title="Report an Issue" open={ticketModal}
        onOk={() => ticketForm.submit()}
        onCancel={() => { setTicketModal(false); ticketForm.resetFields(); }}
        okText="Submit" destroyOnHidden>
        <Form form={ticketForm} layout="vertical" onFinish={handleCreateTicket}>
          <Form.Item name="subject" label="Subject" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="Brief description" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{ required: true, message: "Select priority" }]}>
            <Select placeholder="Select priority">
              <Option value="LOW">Low</Option>
              <Option value="MEDIUM">Medium</Option>
              <Option value="HIGH">High</Option>
              <Option value="CRITICAL">Critical</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true, message: "Required" }]}>
            <Input.TextArea rows={4} placeholder="Describe the issue in detail" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Invoice Detail Modal ── */}
      <Modal
        title="Invoice Details"
        open={Boolean(invoiceDetail)}
        onCancel={() => setInvoiceDetail(null)}
        footer={<Button onClick={() => setInvoiceDetail(null)}>Close</Button>}
 destroyOnHidden width={480}
      >
        {invoiceDetail && (() => {
          const fmtMonth = (ym) => {
            if (!ym) return "—";
            const [y, m] = ym.split("-");
            return new Date(Number(y), Number(m) - 1, 1)
              .toLocaleString("default", { month: "long", year: "numeric" });
          };
          const examCount  = invoiceDetail.total_eye_examinations   ?? 0;
          const lensCount  = invoiceDetail.total_lens_prescriptions  ?? 0;
          const examFeeTotal = parseFloat(invoiceDetail.eye_examination_fee  ?? 0);
          const lensFeeTotal = parseFloat(invoiceDetail.lens_prescription_fee ?? 0);
          const examUnit   = examCount > 0 ? (examFeeTotal / examCount).toFixed(2) : "0.00";
          const lensUnit   = lensCount > 0 ? (lensFeeTotal / lensCount).toFixed(2) : "0.00";
          const INVOICE_COLOR = { DRAFT: "default", SENT: "blue", PAID: "green", OVERDUE: "red" };
          return (
            <div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {[
                    ["Month",  fmtMonth(invoiceDetail.billing_month)],
                    ...(isOphthalmologist ? [
                      ["Eye Examinations",
                        `${examCount} × $${examUnit} = $${examFeeTotal.toFixed(2)}`],
                    ] : []),
                    ["Lens Prescriptions",
                      `${lensCount} × $${lensUnit} = $${lensFeeTotal.toFixed(2)}`],
                    ["Total Amount Due",
                      <Text strong style={{ fontSize: 16 }}>
                        ${parseFloat(invoiceDetail.total_amount ?? 0).toFixed(2)}
                      </Text>],
                    ["Status",
                      <Tag color={INVOICE_COLOR[invoiceDetail.status] ?? "default"}>
                        {invoiceDetail.status}
                      </Tag>],
                  ].map(([label, val]) => (
                    <tr key={label} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "10px 12px", color: "#8c8c8c", width: "40%", fontWeight: 500 }}>{label}</td>
                      <td style={{ padding: "10px 12px" }}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Alert
                type="info" showIcon
                message="Please contact billing@mchusa.com to arrange payment."
                style={{ marginTop: 16 }}
              />
            </div>
          );
        })()}
      </Modal>

      {/* ── Register New Patient Modal ── */}
      <Modal
        title="Register New Patient"
        open={regModal}
        onOk={() => regForm.submit()}
        onCancel={() => { setRegModal(false); regForm.resetFields(); }}
        okText="Register Patient"
        confirmLoading={submitting}

        destroyOnHidden
        width={500}
      >
        <Form form={regForm} layout="vertical" onFinish={handleRegisterPatient}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="first_name" label="First Name"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="First name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="last_name" label="Last Name"
                rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="Last name" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email" label="Email"
            rules={[{ required: true, message: "Required" }, { type: "email", message: "Enter a valid email" }]}>
            <Input placeholder="patient@example.com" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input placeholder="+1 555 000 0000 (optional)" />
          </Form.Item>
          <Form.Item name="password" label="Temporary Password"
            rules={[{ required: true, message: "Required" }, { min: 8, message: "Minimum 8 characters" }]}>
            <Input.Password placeholder="Temporary password" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Network Patients Modal ── */}
      <Modal
        title="All Active Network Patients"
        open={networkPatientsModal}
        onCancel={() => { setNetworkPatientsModal(false); setNetworkSearch(""); }}
        footer={<Button onClick={() => { setNetworkPatientsModal(false); setNetworkSearch(""); }}>Close</Button>}

        width={700}
      >
        <Input.Search
          placeholder="Search by email or PIN"
          allowClear
          onChange={e => setNetworkSearch(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        <Spin spinning={patientsFetching}>
          <Table
            size="small"
            rowKey="id"
            dataSource={patientProfiles.filter(p => {
              if (!networkSearch) return true;
              const q = networkSearch.toLowerCase();
              return (
                (p.user_email ?? "").toLowerCase().includes(q) ||
                (p.pin ?? "").includes(q)
              );
            })}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            locale={{ emptyText: <Empty description="No patients found" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              { title: "Email",  dataIndex: "user_email", key: "email", ellipsis: true },
              { title: "PIN",    dataIndex: "pin",        key: "pin",   width: 130,
                render: v => <Text code>{v ?? "—"}</Text> },
              { title: "Status", key: "status", width: 90,
                render: () => <Tag color="green">Active</Tag> },
            ]}
          />
        </Spin>
      </Modal>

      {/* ── Ticket Detail Modal ── */}
      <Modal title="Ticket Details" open={Boolean(ticketDetail)}
        onCancel={() => setTicketDetail(null)}
        footer={<Button onClick={() => setTicketDetail(null)}>Close</Button>}
 destroyOnHidden width={500}>
        {ticketDetail && (
          <div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {[
                  ["Subject",  ticketDetail.subject],
                  ["Priority", <Tag color={{ LOW: "default", MEDIUM: "blue", HIGH: "orange", CRITICAL: "red" }[ticketDetail.priority]}>{ticketDetail.priority}</Tag>],
                  ["Status",   <Tag color={{ OPEN: "blue", IN_PROGRESS: "orange", RESOLVED: "green", CLOSED: "default" }[ticketDetail.status]}>{ticketDetail.status?.replace(/_/g, " ")}</Tag>],
                  ["Created",  new Date(ticketDetail.created_at).toLocaleString()],
                ].map(([label, val]) => (
                  <tr key={label} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "8px 12px", color: "#8c8c8c", width: "35%" }}>{label}</td>
                    <td style={{ padding: "8px 12px" }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 12 }}>
              <Text strong>Description</Text>
              <div style={{ marginTop: 6, padding: 10, background: "#fafafa", borderRadius: 4, fontSize: 13 }}>
                {ticketDetail.description}
              </div>
            </div>
            {ticketDetail.resolution_notes && (
              <div style={{ marginTop: 12 }}>
                <Text strong>Resolution Notes</Text>
                <div style={{ marginTop: 6, padding: 10, background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4, fontSize: 13 }}>
                  {ticketDetail.resolution_notes}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
}
